/**
 * support.service.js - xSPECTRE support/ticketing API client.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
} from './_core';

export async function getSupportTickets(query = null, token = null) {
  const qs = query && typeof query === 'object' && Object.keys(query).length > 0
    ? `?${new URLSearchParams(query).toString()}`
    : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/support/tickets${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support tickets fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support tickets list');
}

export async function createSupportTicket(payload, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/support/tickets'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload || {}),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support ticket creation failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support ticket create');
}

export async function getSupportTicket(ticketId, token = null) {
  const safeId = Number(ticketId);
  if (!Number.isFinite(safeId)) throw new Error('Valid ticket id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/support/tickets/${encodeURIComponent(String(safeId))}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support ticket detail failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support ticket detail');
}

export async function updateSupportTicket(ticketId, payload, token = null) {
  const safeId = Number(ticketId);
  if (!Number.isFinite(safeId)) throw new Error('Valid ticket id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/support/tickets/${encodeURIComponent(String(safeId))}`),
    {
      method: 'PUT',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload || {}),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support ticket update failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support ticket update');
}

export async function replySupportTicket(ticketId, payload, token = null) {
  const safeId = Number(ticketId);
  if (!Number.isFinite(safeId)) throw new Error('Valid ticket id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/support/tickets/${encodeURIComponent(String(safeId))}/reply`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload || {}),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support ticket reply failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support ticket reply');
}

export async function getSupportNotifications(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/support/notifications'),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support notifications fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support notifications');
}

export async function getSupportKnowledgeBase(query = null, token = null) {
  const qs = query && typeof query === 'object' && Object.keys(query).length > 0
    ? `?${new URLSearchParams(query).toString()}`
    : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/support/knowledge-base${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Support knowledge base fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Support knowledge base');
}
